import type { ComponentPropsWithoutRef, ComponentRef, HTMLAttributes, Ref } from 'react'
import { useCallback, useRef } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'

import { cn } from '../../lib/utils'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

type DialogOverlayProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
    ref?: Ref<ComponentRef<typeof DialogPrimitive.Overlay>>
}

function DialogOverlay({ className, children, ref, ...props }: DialogOverlayProps) {
    return (
        <DialogPrimitive.Overlay
            ref={ref}
            className={cn(
                'fixed inset-0 z-50 bg-[rgb(53_64_82/0.5)] overflow-y-auto flex items-center justify-center data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                className,
            )}
            {...props}
        >
            {children}
        </DialogPrimitive.Overlay>
    )
}

interface DialogContentProps extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
    showCloseIcon?: boolean
    /**
     * Controls whether clicking outside the dialog should close it.
     * @default false - allows closing by clicking outside (standard Radix behavior)
     * Set to false to prevent closing on outside clicks (true modal behavior)
     */
    closeOnOutsideClick?: boolean
    ref?: Ref<ComponentRef<typeof DialogPrimitive.Content>>
}

/**
 * Works around a Radix `FocusScope` interaction (TEC-19022). When a field inside
 * the dialog re-renders synchronously during a Tab keypress — e.g. react-number-format
 * reformatting on blur, or a React Hook Form `mode: 'onBlur'` validation — the browser's
 * native focus transition is interrupted and focus drops to `<body>`. Radix's trapped
 * focus scope then pulls focus back onto the dialog container, which Tab-loops to the
 * first element, so focus "jumps to the top" of the dialog.
 *
 * On `focusout` we record the element the browser intended to focus next
 * (`relatedTarget`) — its own Tab destination, so no tab order is hard-coded. We keep the
 * FIRST destination of the transition and ignore the focus scope's follow-up focus moves.
 * When the scope traps focus onto the dialog container (`focusin` on the container), we put
 * focus back on that intended element. The restore runs in a microtask because a `focus()`
 * call made synchronously inside a focus event handler is ignored by browsers; the microtask
 * runs immediately after the event settles, before paint, so there is no visible flicker.
 *
 * The listeners are wired in a ref callback (not a mount effect) because Radix only mounts
 * the content element when the dialog opens — an effect keyed on mount would run before the
 * node exists and never re-run.
 */
function useDialogFocusGuard() {
    const cleanupRef = useRef<(() => void) | null>(null)

    return useCallback((content: HTMLDivElement | null) => {
        cleanupRef.current?.()
        cleanupRef.current = null
        if (!content) {
            return
        }

        let intended: HTMLElement | null = null
        let frame = 0

        const restoreIntended = () => {
            if (
                intended &&
                intended.isConnected &&
                content.contains(intended) &&
                document.activeElement !== intended
            ) {
                intended.focus()
            }
        }

        const onFocusOut = (event: FocusEvent) => {
            const next = event.relatedTarget
            if (intended || !(next instanceof HTMLElement) || !content.contains(next) || next === content) {
                return
            }
            // Remember the browser's own Tab destination for this transition and forget it
            // once the transition settles, so the next Tab starts clean.
            intended = next
            cancelAnimationFrame(frame)
            frame = requestAnimationFrame(() => {
                intended = null
            })
        }

        const onFocusIn = (event: FocusEvent) => {
            if (event.target === content) {
                queueMicrotask(restoreIntended)
            }
        }

        content.addEventListener('focusout', onFocusOut)
        content.addEventListener('focusin', onFocusIn)
        cleanupRef.current = () => {
            content.removeEventListener('focusout', onFocusOut)
            content.removeEventListener('focusin', onFocusIn)
            cancelAnimationFrame(frame)
        }
    }, [])
}

function DialogContent({
    className,
    children,
    showCloseIcon = true,
    closeOnOutsideClick = false,
    onInteractOutside,
    ref,
    ...props
}: DialogContentProps) {
    const guardRef = useDialogFocusGuard()

    const handleInteractOutside: ComponentPropsWithoutRef<
        typeof DialogPrimitive.Content
    >['onInteractOutside'] = (event) => {
        // Call custom handler if provided
        if (onInteractOutside) {
            onInteractOutside(event)
        }
        // Prevent closing if closeOnOutsideClick is false
        if (!closeOnOutsideClick) {
            event.preventDefault()
        }
    }

    return (
        <DialogPortal>
            <DialogOverlay>
                <DialogPrimitive.Content
                    ref={(node) => {
                        guardRef(node)
                        if (typeof ref === 'function') {
                            ref(node)
                        } else if (ref) {
                            ref.current = node
                        }
                    }}
                    className={cn(
                        'dialog-content',
                        'relative overflow-y-auto z-50 grid w-full min-w-0 max-h-[85vh] max-w-[360px] gap-4 border border-gray-200 bg-background p-4 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 rounded-lg',
                        className,
                    )}
                    onInteractOutside={handleInteractOutside}
                    {...props}
                >
                    {children}
                    {showCloseIcon && (
                        <DialogPrimitive.Close
                            data-testid="dialog-close-button"
                            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
                        >
                            <div className="h-9 w-9 border border-lp-dark1 stroke-[1.2px] rounded-full flex justify-center items-center">
                                <X className="text-lp-dark1" />
                            </div>
                            <span className="sr-only">Close</span>
                        </DialogPrimitive.Close>
                    )}
                </DialogPrimitive.Content>
            </DialogOverlay>
        </DialogPortal>
    )
}

function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
    return (
        <div
            className={cn('flex flex-col space-y-1.5 text-center sm:text-left', className)}
            {...props}
        />
    )
}

function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
    return (
        <div
            className={cn(
                'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
                className,
            )}
            {...props}
        />
    )
}

type DialogTitleProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
    ref?: Ref<ComponentRef<typeof DialogPrimitive.Title>>
}

function DialogTitle({ className, ref, ...props }: DialogTitleProps) {
    return (
        <DialogPrimitive.Title
            ref={ref}
            className={cn('text-lg font-semibold leading-none tracking-tight', className)}
            {...props}
        />
    )
}

type DialogDescriptionProps = ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & {
    ref?: Ref<ComponentRef<typeof DialogPrimitive.Description>>
}

function DialogDescription({ className, ref, ...props }: DialogDescriptionProps) {
    return (
        <DialogPrimitive.Description
            ref={ref}
            className={cn('text-sm text-muted-foreground', className)}
            {...props}
        />
    )
}

export {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogPortal,
    DialogTitle,
    DialogTrigger,
}
